import { z } from "zod";
import { type CommittedDelta, CommittedDeltaSchema } from "./deltas";

/** A world's record stored as a header line, then one delta per line, in revision order. */
export const WORLD_ARTIFACT_CONTENT_TYPE = "application/x-ndjson";

const WORLD_ARTIFACT_FORMAT = "pax-world-record";

/** The header line, for writers that stream a record line by line. */
export const WORLD_ARTIFACT_HEADER = JSON.stringify({ format: WORLD_ARTIFACT_FORMAT });

const HeaderSchema = z.strictObject({ format: z.literal(WORLD_ARTIFACT_FORMAT) });

export function parseWorldArtifact(text: string): CommittedDelta[] {
  const deltas = linesOf(text).map(line => CommittedDeltaSchema.parse(JSON.parse(line)));
  assertWellOrdered(deltas);
  return deltas;
}

export function encodeWorldArtifact(deltas: readonly CommittedDelta[]): string {
  if (deltas.length === 0) throw new Error("world artifact must contain deltas");
  assertWellOrdered(deltas);
  return [
    WORLD_ARTIFACT_HEADER,
    ...deltas.map(delta => JSON.stringify(CommittedDeltaSchema.parse(delta))),
  ]
    .map(line => `${line}\n`)
    .join("");
}

/** The delta lines after the header, once the header and the lines' shape check out. */
function linesOf(text: string): string[] {
  if (!text.endsWith("\n")) throw new Error("world artifact has an incomplete final line");
  const [first, ...lines] = text.slice(0, -1).split("\n");
  const parsed = first?.startsWith("{") ? HeaderSchema.safeParse(JSON.parse(first)) : undefined;
  if (!parsed?.success) {
    throw new Error(`world artifact does not start with a ${WORLD_ARTIFACT_FORMAT} header`);
  }
  if (lines.length === 0) throw new Error("world artifact must contain deltas");
  if (lines.some(line => line.length === 0)) throw new Error("world artifact has an empty line");
  return lines;
}

function assertWellOrdered(deltas: readonly CommittedDelta[]): void {
  for (const [index, delta] of deltas.entries()) {
    if (delta.revision !== index + 1) {
      throw new Error(`world artifact expected revision ${index + 1} and found ${delta.revision}`);
    }
  }
}
